import {
  ALARM_ID_DECIMAL,
  ALARM_ID_HEXADECIMAL,
  EVENTS_SEARCH_ERROR_MESSAGES,
} from '../constants';
import {
  EventsSearchObject,
  ColumnType,
  EVENT_SEARCH_KEY_TYPES,
} from '../types';

// TODO: Create unit tests for the all the functions in the file

export const isFreeTextObject = (obj: EventsSearchObject): boolean => {
  return Object.keys(obj ?? {}).some(
    (k) => !EVENT_SEARCH_KEY_TYPES.includes(k as keyof EventsSearchObject),
  );
};

export const eventSearchObjectToQueryParams = (
  searchObject: EventsSearchObject,
): string => {
  let queryString = '';

  if (isFreeTextObject(searchObject)) {
    Object.values(searchObject ?? {}).forEach((values) => {
      values.forEach((v) => {
        queryString += `&freetext=${v}`;
      });
    });
  } else {
    for (const [key, values] of Object.entries(searchObject ?? {})) {
      values.forEach((v) => {
        queryString += `&${key}=${v}`;
      });
    }
  }

  return queryString;
};

export const parseEventsSearchInput = (input: string): EventsSearchObject => {
  const columnKeys = EVENT_SEARCH_KEY_TYPES.join('|');
  const columnKeyRegex = new RegExp(`^(${columnKeys}):`);
  const items = input
    .toLowerCase()
    .split(new RegExp(`\\s+(?=(${columnKeys}):(\\S|$))`))
    .filter(Boolean);

  if (!items.length) return null;

  const columnItems = items.filter((item) => columnKeyRegex.test(item));

  if (columnItems.length) {
    const result = {};

    for (const item of columnItems) {
      const colonIndex = item.indexOf(':');
      const key = item.slice(0, colonIndex) as ColumnType;
      const value = item
        .slice(colonIndex + 1)
        .trim()
        .replace(/,+$/, '');

      if (key in result) {
        result[key] = [...result[key], value];
      } else {
        result[key] = [value];
      }
    }

    return result as EventsSearchObject;
  }

  const keywords = items
    .join(' ')
    .split(',')
    .map((keyword) => keyword.trim())
    .filter(Boolean);

  if (!keywords.length) return null;

  return { 0: keywords };
};

export const validateEventSearchObjects = (
  prev: EventsSearchObject,
  next: EventsSearchObject,
) => {
  const prevKeys = Object.keys(prev ?? {});

  const hasEmptyRule =
    !next ||
    Object.values(next ?? {}).some(
      (v) => v.length === 0 || v.some((item) => !item),
    );

  if (hasEmptyRule) throw new Error(EVENTS_SEARCH_ERROR_MESSAGES.EMPTY_RULES);

  if (prevKeys.length && isFreeTextObject(prev) !== isFreeTextObject(next))
    throw new Error(EVENTS_SEARCH_ERROR_MESSAGES.KEY_AND_FREE_TEXT_SEARCH);

  return true;
};

export const mergeEventSearchObjects = (
  prev: EventsSearchObject,
  next: EventsSearchObject,
): EventsSearchObject => {
  const isFreeText = isFreeTextObject(next);

  if (isFreeText) {
    const prevValues = Object.values(prev ?? {});
    const nextValues = Object.values(next ?? {});

    return Object.fromEntries(
      [...prevValues, ...nextValues].map((v, i) => [i.toString(), v]),
    );
  }

  const merged: EventsSearchObject = { ...prev };
  for (const [key, value] of Object.entries(next ?? {})) {
    if (key in merged) {
      merged[key] = [...merged[key], ...value];
    } else {
      merged[key] = value;
    }
  }
  return merged;
};

const isDigit = (char: string): boolean => char >= '0' && char <= '9';

const isHexDigit = (char: string): boolean =>
  isDigit(char) || (char >= 'a' && char <= 'f');

const isDecimalEventId = (id: string): boolean =>
  id.length > 0 && [...id].every(isDigit);

const hasHexBody = (value: string): boolean =>
  value.length > 0 && [...value].every(isHexDigit);

const isPrefixedHexEventId = (id: string): boolean => {
  const normalized = id.toLowerCase();

  return normalized.startsWith('0x') && hasHexBody(normalized.slice(2));
};

const isHexEventId = (id: string): boolean => {
  const normalized = id.toLowerCase();
  const hexBody = normalized.startsWith('0x')
    ? normalized.slice(2)
    : normalized;

  return hasHexBody(hexBody);
};

export const isValidAlarmId = (
  id: string,
  alarmIdRepresentation: string,
): boolean => {
  if (alarmIdRepresentation === ALARM_ID_HEXADECIMAL) {
    return isHexEventId(id);
  }

  return isDecimalEventId(id) || isPrefixedHexEventId(id);
};

export const isInvalidEventIdSearch = (
  search: EventsSearchObject,
  alarmIdRepresentation: string = ALARM_ID_DECIMAL,
): boolean => {
  const idValues = search?.id;

  if (!idValues?.length) return false;

  return idValues.some((id) => !isValidAlarmId(id, alarmIdRepresentation));
};

export const isEventsSearchBadRequest = (error: unknown): boolean => {
  if (!error || typeof error !== 'object' || !('status' in error)) {
    return false;
  }

  return (error as { status?: unknown }).status === 400;
};
